/**
 * Draft mode utilities for ingredient editing
 */

import {EditorView} from "@codemirror/view";
import {Token} from "../models/editor.types";
import {tokensField, setTokensEffect} from "../state/editor.state";

/**
 * Get the current draft token (if any)
 */
export function getDraft(tokens: Token[]): Token | undefined {
  return tokens.find((t) => t.type === "ingredient_draft");
}

/**
 * Get draft near cursor position (allows some flexibility)
 */
export function getDraftNearCursor(tokens: Token[], pos: number): Token | undefined {
  return (
    tokens.find((t) => t.type === "ingredient_draft" && t.from <= pos && pos <= t.to) ||
    tokens.find((t) => t.type === "ingredient_draft" && t.from <= pos - 1 && pos - 1 <= t.to) ||
    tokens.find((t) => t.type === "ingredient_draft" && t.to === pos) ||
    tokens.find((t) => t.type === "ingredient_draft" && t.to === pos - 1)
  );
}

/**
 * Cancel draft - empty draft is removed, non-empty is kept as plain text
 */
export function cancelAnyDraft(view: EditorView, log: (msg: string, data?: any) => void): boolean {
  const state = view.state;
  const tokens = state.field(tokensField);

  const draft = getDraft(tokens);
  if (!draft) return false;

  const text = state.doc.sliceString(draft.from, draft.to);
  const trimmed = text.replace(/\s+$/g, "");
  const next = tokens.filter((t) => t !== draft);

  if (trimmed.length === 0) {
    log("⛔ cancel draft (empty)", {draft});
    view.dispatch({effects: setTokensEffect.of(next)});
    return true;
  }

  const newTo = draft.from + trimmed.length;
  const hasTrailing = newTo < draft.to;

  log("⛔ cancel draft (keep plain text)", {draft, text, trimmed});

  if (hasTrailing) {
    view.dispatch({
      changes: {from: newTo, to: draft.to, insert: ""},
      effects: setTokensEffect.of(next),
      selection: {anchor: newTo, head: newTo},
      scrollIntoView: true,
    });
  } else {
    view.dispatch({
      effects: setTokensEffect.of(next),
      selection: {anchor: draft.to, head: draft.to},
      scrollIntoView: true,
    });
  }

  return true;
}

/**
 * Commit draft to permanent ingredient token
 */
export function commitDraft(
  view: EditorView,
  log: (msg: string, data?: any) => void
): { ok: boolean; endPos?: number } {
  const state = view.state;
  const tokens = state.field(tokensField);
  const pos = state.selection.main.head;

  const draft = getDraftNearCursor(tokens, pos);
  if (!draft) return {ok: false};

  const text = state.doc.sliceString(draft.from, draft.to);
  const trimmed = text.replace(/\s+$/g, "");
  const newTo = draft.from + trimmed.length;

  const next = tokens.filter((t) => t !== draft);
  if (newTo > draft.from) {
    next.push({from: draft.from, to: newTo, type: "ingredient"});
  }

  log("✅ commit draft -> ingredient chip", {draft, text, trimmed, newTo});
  view.dispatch({effects: setTokensEffect.of(next)});

  return {ok: true, endPos: newTo};
}

/**
 * Auto-detect quantity and start draft mode
 */
const START_RE = /\b(\d+(?:[.,]\d+)?)\s*(kg|g|mg|l|ml|el|tl|stk|stück|prise)\s+$/i;

export function tryStartDraftAtCursor(
  view: EditorView,
  log: (msg: string, data?: any) => void
): boolean {
  const state = view.state;
  const tokens = state.field(tokensField);

  if (getDraft(tokens)) return false;

  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);
  const before = state.doc.sliceString(line.from, pos);

  const m = before.match(START_RE);
  if (!m) return false;

  const full = m[0];
  const trimmed = full.replace(/\s+$/g, "");
  const qtyFrom = pos - full.length;
  const qtyTo = qtyFrom + trimmed.length;

  const draftFrom = pos;
  const draftTo = pos;

  const next = tokens.filter((t) => t.to <= qtyFrom || t.from >= draftTo);

  next.push({from: qtyFrom, to: qtyTo, type: "quantity"});
  next.push({from: draftFrom, to: draftTo, type: "ingredient_draft"});

  log("🟢 start draft", {qtyFrom, qtyTo, draftFrom, draftTo});
  view.dispatch({effects: setTokensEffect.of(next)});
  return true;
}

/**
 * Manually start ingredient draft at cursor
 */
export function startIngredientDraftAtCursor(
  view: EditorView,
  log: (msg: string, data?: any) => void
): boolean {
  const state = view.state;
  const tokens = state.field(tokensField);

  if (getDraft(tokens)) return false;

  const pos = state.selection.main.head;
  const next = tokens.slice();
  next.push({from: pos, to: pos, type: "ingredient_draft"});

  log("🟢 manual draft start (@)", {pos});
  view.dispatch({effects: setTokensEffect.of(next)});
  return true;
}

/**
 * Add ingredient token at cursor for the current word
 */
export function addIngredientTokenAtCursor(
  view: EditorView,
  log: (msg: string, data?: any) => void
): boolean {
  const state = view.state;
  const pos = state.selection.main.head;

  const isWordChar = (ch: string) => /[\p{L}\p{N}_\-]/u.test(ch);

  let from = pos;
  while (from > 0) {
    const c = state.doc.sliceString(from - 1, from);
    if (!isWordChar(c)) break;
    from--;
  }

  let to = pos;
  while (to < state.doc.length) {
    const c = state.doc.sliceString(to, to + 1);
    if (!isWordChar(c)) break;
    to++;
  }

  if (to <= from) return false;

  const tokens = state.field(tokensField);
  const next = tokens.filter((t) => !(t.type === "ingredient" && t.from === from && t.to === to));
  next.push({from, to, type: "ingredient"});

  log("✅ mark word as ingredient", {from, to, text: state.doc.sliceString(from, to)});
  view.dispatch({effects: setTokensEffect.of(next)});
  return true;
}
